import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Clock, XCircle } from 'lucide-react';
import { signOut as supabaseSignOut } from '../../services/authentication/supabaseService';
import { useAuth } from '../../services/authentication/authContext';
import { Header } from '../pages/landingpage/sections/Header';

export default function PendingApprovalPage() {
  const { backendUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Redirect if user is not pending/rejected staff
  if (!backendUser || backendUser.role !== 'staff' || (backendUser.access_request !== 'pending' && backendUser.access_request !== 'rejected')) {
    return <Navigate to="/dashboard" replace />;
  }

  const user = backendUser;
  const isPending = user.access_request === 'pending';
  const isRejected = user.access_request === 'rejected';

  const handleLogout = async () => {
    try {
      await supabaseSignOut();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout error', err);
    }
  };

  return (
    <div className="relative overflow-hidden min-h-screen bg-landingPageBG dark:bg-landingPageBG-dark text-white dark:text-white">
      <Header />
      <div className="flex flex-col justify-center items-center min-h-screen px-5 md:px-8 py-20">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-white/10 dark:border-white/10 p-8 bg-white/5 dark:bg-white/5 backdrop-blur-sm">
            {/* Status Icon */}
            <div className="flex justify-center mb-6">
              {isPending && (
                <div className="w-16 h-16 rounded-full bg-yellow-500/10 dark:bg-yellow-500/10 flex items-center justify-center">
                  <Clock className="w-8 h-8 text-yellow-400 dark:text-yellow-400" />
                </div>
              )}
              {isRejected && (
                <div className="w-16 h-16 rounded-full bg-red-500/10 dark:bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-400 dark:text-red-400" />
                </div>
              )}
            </div>

            {/* Title */}
            <h1 className="text-2xl font-semibold text-white dark:text-white text-center mb-2">
              {isPending ? t('pendingApproval.title') : t('pendingApproval.rejectedTitle')}
            </h1>

            {/* Message */}
            <p className="text-white/70 dark:text-white/70 text-center mb-6">
              {isPending ? t('pendingApproval.message') : t('pendingApproval.rejectedMessage')}
            </p>

            {/* User Info Card */}
            <div className="bg-white/5 dark:bg-white/5 rounded-xl p-4 mb-6 border border-white/10 dark:border-white/10">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-white/50 dark:text-white/50">{t('pendingApproval.name')}</span>
                  <span className="text-sm font-medium text-white dark:text-white">
                    {user.name || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-white/50 dark:text-white/50">{t('pendingApproval.email')}</span>
                  <span className="text-sm font-medium text-white dark:text-white">
                    {user.email}
                  </span>
                </div>
                {user.restaurant_role && (
                  <div className="flex justify-between">
                    <span className="text-sm text-white/50 dark:text-white/50">{t('pendingApproval.role')}</span>
                    <span className="text-sm font-medium text-white dark:text-white">
                      {user.restaurant_role}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-white/50 dark:text-white/50">{t('pendingApproval.status')}</span>
                  <span className={`text-sm font-medium ${
                    isPending ? 'text-yellow-400 dark:text-yellow-400' : 'text-red-400 dark:text-red-400'
                  }`}>
                    {isPending ? t('staff.accessRequest.pending') : t('staff.accessRequest.rejected')}
                  </span>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/20 dark:border-white/20 text-white dark:text-white hover:bg-white/10 dark:hover:bg-white/10 transition font-medium"
            >
              <LogOut className="w-5 h-5" />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
